// HistoryModal Component - Modal for viewing full conversation history

import { useRef, useEffect } from 'react';
import type { HistoryItem } from '../../types/agent';
import { HistoryItemComponent } from './HistoryItem';
import { useEscapeKey } from '../../hooks/useEscapeKey';
import styles from './HistoryModal.module.css';

// Close icon
function CloseIcon() {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
    </svg>
  );
}

interface HistoryModalProps {
  isOpen: boolean;
  onClose: () => void;
  historyItems: HistoryItem[];
  highlightedIndex: number | null;
}

export function HistoryModal({ isOpen, onClose, historyItems, highlightedIndex }: HistoryModalProps) {
  const itemRefs = useRef<(HTMLDivElement | null)[]>([]);
  const contentRef = useRef<HTMLDivElement>(null);

  // Scroll to highlighted item when modal opens or highlighted index changes
  useEffect(() => {
    if (isOpen && highlightedIndex !== null && itemRefs.current[highlightedIndex]) {
      // Small delay to ensure modal is rendered
      setTimeout(() => {
        itemRefs.current[highlightedIndex]?.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        });
      }, 100);
    }
  }, [isOpen, highlightedIndex]);

  // Close on escape key
  useEscapeKey(onClose, isOpen);

  if (!isOpen) return null;

  return (
    <div className={styles.modalOverlay} onClick={onClose}>
      <div className={styles.modalContainer} onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className={styles.modalHeader}>
          <div className={styles.headerTitle}>
            <h3>Conversation History</h3>
            <span className={styles.headerHint}>Click any item to expand details</span>
          </div>
          <button onClick={onClose} className={styles.closeButton} aria-label="Close modal">
            <CloseIcon />
          </button>
        </div>

        {/* Content */}
        <div className={styles.modalContent} ref={contentRef}>
          {historyItems.length === 0 ? (
            <div className={styles.emptyState}>
              No conversation history yet. Start chatting to build history.
            </div>
          ) : (
            <div className={styles.itemsList}>
              {historyItems.map((item, index) => (
                <div
                  key={index}
                  ref={(el) => { itemRefs.current[index] = el; }}
                >
                  <HistoryItemComponent
                    item={item}
                    isHighlighted={highlightedIndex === index}
                  />
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className={styles.modalFooter}>
          <div className={styles.itemCount}>
            {historyItems.length} items in conversation history
          </div>
          <div className={styles.footerHint}>
            Tip: Click any message in the chat to jump here
          </div>
        </div>
      </div>
    </div>
  );
}

export default HistoryModal;
